import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../lib/telegram';
import { Icon } from './Icon';
import { Sheet } from './Sheet';
import { Avatar } from './ui';

const SEEN_KEY = 'expenditure.guide.seen';

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true; // No storage: don't open it on every visit.
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // ignore
  }
}

/** One person in the example. */
function Person({ name }: { name: string }) {
  return (
    <span className="guide-person">
      <Avatar name={name} small />
      <span>{name}</span>
    </span>
  );
}

/** "Lina → Dara  $10", as the Settle up screen shows it. */
function Owes({ from, to, amount, muted }: { from: string; to: string; amount: string; muted?: boolean }) {
  return (
    <div className={`guide-owes ${muted ? 'guide-owes--muted' : ''}`}>
      <Avatar name={from} small />
      <span className="guide-owes-names">
        {from} <span className="transfer-arrow">→</span> {to}
      </span>
      <strong className="num">{amount}</strong>
    </div>
  );
}

/**
 * "How it works": the whole flow in six steps with one worked example
 * (Dara pays $30 for dinner for three), with the weight on settling up.
 */
export function GuideSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const [dara, lina, sok] = [t('guide.dara'), t('guide.lina'), t('guide.sok')];
  const steps: Array<{ title: string; body: string; art: ReactNode }> = [
    {
      title: t('guide.s1Title'),
      body: t('guide.s1Body', { dara }),
      art: (
        <>
          <div className="guide-bill">
            <span className="icon-tile icon-tile--ink" aria-hidden>
              <Icon name="food" size={18} />
            </span>
            <span className="grow">
              <strong>{t('guide.dinner')}</strong>
              <small>{t('group.paidBy', { name: dara })}</small>
            </span>
            <strong className="num">$30.00</strong>
          </div>
          <div className="guide-people">
            <Person name={dara} />
            <Person name={lina} />
            <Person name={sok} />
          </div>
        </>
      ),
    },
    {
      title: t('guide.s2Title'),
      body: t('guide.s2Body', { dara, lina, sok }),
      art: (
        <>
          <div className="guide-shares num">
            <span>$10</span>
            <span>$10</span>
            <span>$10</span>
          </div>
          <Owes from={lina} to={dara} amount="$10.00" />
          <Owes from={sok} to={dara} amount="$10.00" />
        </>
      ),
    },
    {
      title: t('guide.s3Title'),
      body: t('guide.s3Body', { dara, lina, sok }),
      art: (
        <>
          <Owes from={lina} to={dara} amount="$10.00" muted />
          <Owes from={dara} to={sok} amount="$10.00" muted />
          <div className="guide-becomes" aria-hidden>
            <Icon name="arrowRight" size={18} />
          </div>
          <Owes from={lina} to={sok} amount="$10.00" />
        </>
      ),
    },
    {
      title: t('guide.s4Title'),
      body: t('guide.s4Body', { dara, settle: t('settle.title'), payKhqr: t('settle.payKhqr') }),
      art: (
        <>
          <Owes from={t('common.you')} to={dara} amount="$10.00" />
          <div className="guide-buttons">
            <span className="btn btn--primary btn--small">{t('settle.payKhqr')}</span>
            <span className="guide-or">{t('pro.or')}</span>
            <span className="guide-cash">{t('guide.cash')}</span>
          </div>
        </>
      ),
    },
    {
      title: t('guide.s5Title'),
      body: t('guide.s5Body', { dara, iPaid: t('settle.iPaid'), markPaid: t('settle.markPaid') }),
      art: (
        <>
          <div className="guide-buttons">
            <span className="btn btn--soft btn--small">{t('settle.markPaid')}</span>
          </div>
          <div className="guide-done">
            <span className="icon-tile icon-tile--pos" aria-hidden>
              <Icon name="check" size={18} />
            </span>
            <span>{t('settle.recorded')}</span>
          </div>
        </>
      ),
    },
    {
      title: t('guide.s6Title'),
      body: t('guide.s6Body', { request: t('settle.request'), allSettled: t('settle.allSettled') }),
      art: (
        <>
          <Owes from={lina} to={t('common.you')} amount="$10.00" />
          <div className="guide-buttons">
            <span className="btn btn--primary btn--small">{t('settle.request')}</span>
          </div>
          <div className="guide-done">
            <span className="icon-tile icon-tile--pos" aria-hidden>
              <Icon name="checkCircle" size={18} />
            </span>
            <span>{t('settle.allSettled')}</span>
          </div>
        </>
      ),
    },
  ];

  const current = steps[step]!;
  const last = step === steps.length - 1;
  const go = (to: number) => {
    haptic.select();
    setStep(to);
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('guide.title')}>
      <div className="guide">
        <div className="guide-art" key={step} aria-hidden>
          {current.art}
        </div>
        <div className="guide-text" aria-live="polite">
          <div className="guide-count num">{t('guide.step', { n: step + 1, total: steps.length })}</div>
          <h3>{current.title}</h3>
          <p>{current.body}</p>
        </div>
        <div className="guide-dots" aria-hidden>
          {steps.map((s, i) => (
            <i key={s.title} className={i === step ? 'on' : undefined} />
          ))}
        </div>
        <div className="row">
          {step > 0 && (
            <button type="button" className="btn btn--ghost grow" onClick={() => go(step - 1)}>
              {t('guide.back')}
            </button>
          )}
          <button type="button" className="btn btn--primary grow" onClick={() => (last ? onClose() : go(step + 1))}>
            {last ? t('guide.done') : t('guide.next')}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** The guide plus its state: opens by itself the first time (`autoOpen`), then only on request. */
export function useGuide(autoOpen = false) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (autoOpen && !readSeen()) setOpen(true);
  }, [autoOpen]);
  const close = () => {
    markSeen();
    setOpen(false);
  };
  return { open, show: () => setOpen(true), sheet: <GuideSheet open={open} onClose={close} /> };
}

/** "How it works" button for a page header. */
export function GuideButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="btn btn--soft btn--small" onClick={onClick}>
      <Icon name="help" size={16} />
      {t('guide.button')}
    </button>
  );
}
